import { TelemetryCatChannelType } from '../types/telemetry-dashboard.types';
import { formatParameterLabel } from './telemetry-helpers.utils';

export type TelemetryCatalogResponse = Record<
  string,
  {
    label?: string;
    params?: Record<string, TelemetryCatChannelType>;
  }
>;

export const CAT: TelemetryCatalogResponse = {};

/**
 * Safely extracts channel parameter dictionary for a subsystem (supporting params, channels, parameters, and case-insensitive subsystem keys)
 */
export function getSubParams(sub: string): Record<string, TelemetryCatChannelType> {
  if (!sub) return {};
  if (CAT[sub]) {
    const s = CAT[sub] as any;
    return s.params || s.channels || s.parameters || {};
  }
  const subLower = sub.toLowerCase();
  const matchedKey = Object.keys(CAT).find((k) => k.toLowerCase() === subLower);
  if (matchedKey && CAT[matchedKey]) {
    const s = CAT[matchedKey] as any;
    return s.params || s.channels || s.parameters || {};
  }
  return {};
}

/**
 * Dynamically updates the global CAT dictionary with parameters returned from the API.
 */
export function updateTelemetryCatalogFromApi(apiCatalog: TelemetryCatalogResponse) {
  if (!apiCatalog || typeof apiCatalog !== 'object') return;

  try {
    sessionStorage.setItem('telemetry_catalog_cache', JSON.stringify(apiCatalog));
  } catch {}

  // Reset CAT object keys and populate exclusively with API parameters
  Object.keys(CAT).forEach((key) => delete CAT[key]);
  Object.assign(CAT, apiCatalog);

  // Auto-append subsystem, paramId, label, uniqueId, and allowed visualizations to channels
  Object.entries(CAT).forEach(([subKey, g]: [string, any]) => {
    if (!g || typeof g !== 'object') return;
    const paramsMap = g.params || g.channels || g.parameters || (typeof g === 'object' && !g.label ? g : null);
    if (paramsMap && typeof paramsMap === 'object') {
      if (!g.params) g.params = paramsMap;
      Object.entries(paramsMap).forEach(([pKey, d]: [string, any]) => {
        if (d && typeof d === 'object') {
          d.subsystem = d.subsystem || subKey;
          d.paramId = d.paramId || pKey;
          d.paramName = d.paramName || pKey;
          d.label = d.label || formatParameterLabel(d.paramName || pKey);
          d.uniqueId = d.uniqueId || d.uniqueid || (d.subsystem && d.paramId ? `${d.subsystem}:${d.paramId}` : `${d.subsystem}:${d.label}`);

          if (['cont', 'pct', 'multi'].includes(d.kind)) {
            if (!d.allowed) d.allowed = ['line', 'area', 'kpi'];
            if (!d.allowed.includes('table')) d.allowed = [...d.allowed, 'table'];
            if (!d.allowed.includes('cchart')) d.allowed = [...d.allowed, 'cchart'];
          }
        }
      });
    }
  });
}

/**
 * Auto-recover catalog from session storage if available
 */
export function hydrateCatalogFromCache() {
  try {
    const cached = sessionStorage.getItem('telemetry_catalog_cache');
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && typeof parsed === 'object' && Object.keys(parsed).length > 0) {
        if (Object.keys(CAT).length === 0) {
          updateTelemetryCatalogFromApi(parsed);
        }
      }
    }
  } catch {}
}

// Immediately attempt hydration on module load
hydrateCatalogFromCache();
